import { useEffect, useState, useCallback } from 'react';
import { AdminLayout } from '../components/admin-layout';
import { Card, Table, Th, Td, Button, Input, Select, Badge, Pagination } from '../components/ui';
import { getAuditLogs } from '../features/audit-log/api';
import type { AuditLogItem } from '@app/shared';

const aksiBadgeColor: Record<string, 'green' | 'red' | 'blue' | 'gray'> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
  lock: 'blue',
  unlock: 'green',
  finalize: 'blue',
  adjust: 'blue',
  login: 'green',
  logout: 'gray',
};

const aksiLabels: Record<string, string> = {
  create: 'Buat',
  update: 'Perbarui',
  delete: 'Hapus',
  lock: 'Kunci',
  unlock: 'Buka Kunci',
  finalize: 'Finalisasi',
  adjust: 'Penyesuaian',
  login: 'Login',
  logout: 'Logout',
};

const entitasOptions = [
  { value: '', label: 'Semua Entitas' },
  { value: 'periode_absensi', label: 'Periode Absensi' },
  { value: 'periode_honor', label: 'Periode Honor' },
  { value: 'slip_honorarium', label: 'Slip Honorarium' },
  { value: 'absensi', label: 'Absensi' },
  { value: 'pegawai', label: 'Pegawai' },
  { value: 'akun', label: 'Akun' },
  { value: 'pengaturan_sistem', label: 'Pengaturan Sistem' },
  { value: 'pengaturan_lembaga', label: 'Pengaturan Lembaga' },
  { value: 'insentif_kegiatan', label: 'Insentif Kegiatan' },
  { value: 'finalisasi_honor', label: 'Finalisasi Honor' },
  { value: 'total_jp', label: 'Total JP Mengajar' },
];

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function tryParseDetail(detail: string | null): Record<string, unknown> | null {
  if (!detail) return null;
  try {
    return JSON.parse(detail);
  } catch {
    return null;
  }
}

function formatDetail(detail: string | null): string {
  const parsed = tryParseDetail(detail);
  if (!parsed) return detail ?? '—';
  return Object.entries(parsed)
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
    .join(', ');
}

export function AuditLogPage() {
  const [rows, setRows] = useState<AuditLogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // Filters
  const [filterAksi, setFilterAksi] = useState('');
  const [filterEntitas, setFilterEntitas] = useState('');
  const [filterPegawaiId, setFilterPegawaiId] = useState('');
  const [filterTanggalDari, setFilterTanggalDari] = useState('');
  const [filterTanggalSampai, setFilterTanggalSampai] = useState('');
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAuditLogs({
        page,
        limit,
        aksi: filterAksi || undefined,
        entitas: filterEntitas || undefined,
        pegawai_id: filterPegawaiId ? Number(filterPegawaiId) : undefined,
        tanggal_dari: filterTanggalDari || undefined,
        tanggal_sampai: filterTanggalSampai || undefined,
        search: search || undefined,
        sort_by: 'dibuat_pada',
        sort_dir: 'desc',
      });
      setRows(result.rows);
      setTotal(result.meta?.total ?? 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat data audit log');
    } finally {
      setLoading(false);
    }
  }, [
    page,
    filterAksi,
    filterEntitas,
    filterPegawaiId,
    filterTanggalDari,
    filterTanggalSampai,
    search,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilter = () => {
    setPage(1);
    fetchData();
  };

  const handleReset = () => {
    setFilterAksi('');
    setFilterEntitas('');
    setFilterPegawaiId('');
    setFilterTanggalDari('');
    setFilterTanggalSampai('');
    setSearch('');
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <AdminLayout title="Audit Log">
      <div style={{ padding: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem' }}>
          📋 Audit Log
        </h1>

        {/* Filter Bar */}
        <Card style={{ marginBottom: '1rem', padding: '1rem' }}>
          <div
            style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' }}
          >
            <div style={{ minWidth: 140 }}>
              <label
                style={{ fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}
              >
                Aksi
              </label>
              <Select value={filterAksi} onChange={(e) => setFilterAksi(e.target.value)}>
                <option value="">Semua Aksi</option>
                {Object.entries(aksiLabels).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </Select>
            </div>
            <div style={{ minWidth: 160 }}>
              <label
                style={{ fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}
              >
                Entitas
              </label>
              <Select value={filterEntitas} onChange={(e) => setFilterEntitas(e.target.value)}>
                {entitasOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>
            <div style={{ minWidth: 120 }}>
              <label
                style={{ fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}
              >
                ID Pegawai
              </label>
              <Input
                type="number"
                placeholder="ID Pegawai"
                value={filterPegawaiId}
                onChange={(e) => setFilterPegawaiId(e.target.value)}
              />
            </div>
            <div style={{ minWidth: 150 }}>
              <label
                style={{ fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}
              >
                Tanggal Dari
              </label>
              <Input
                type="date"
                value={filterTanggalDari}
                onChange={(e) => setFilterTanggalDari(e.target.value)}
              />
            </div>
            <div style={{ minWidth: 150 }}>
              <label
                style={{ fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}
              >
                Tanggal Sampai
              </label>
              <Input
                type="date"
                value={filterTanggalSampai}
                onChange={(e) => setFilterTanggalSampai(e.target.value)}
              />
            </div>
            <div style={{ minWidth: 180 }}>
              <label
                style={{ fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}
              >
                Cari
              </label>
              <Input
                placeholder="Cari entitas, alasan, detail..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
              <Button onClick={handleFilter}>🔍 Filter</Button>
              <Button onClick={handleReset} variant="secondary">
                ↩ Reset
              </Button>
            </div>
          </div>
        </Card>

        {error && (
          <div
            style={{
              color: 'var(--color-danger)',
              marginBottom: '1rem',
              padding: '0.75rem',
              background: 'var(--color-danger-bg)',
              borderRadius: 8,
            }}
          >
            {error}
          </div>
        )}

        {/* Table */}
        <Card>
          <Table>
            <thead>
              <tr>
                <Th>Waktu</Th>
                <Th>Aksi</Th>
                <Th>Entitas</Th>
                <Th>Entitas ID</Th>
                <Th>Pegawai</Th>
                <Th>Alasan</Th>
                <Th>Detail</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <Td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>
                    Memuat data...
                  </Td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <Td
                    colSpan={7}
                    style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-muted)' }}
                  >
                    Tidak ada data audit log
                  </Td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <Td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                      {formatDateTime(row.dibuat_pada)}
                    </Td>
                    <Td>
                      <Badge color={aksiBadgeColor[row.aksi] ?? 'gray'}>
                        {aksiLabels[row.aksi] ?? row.aksi}
                      </Badge>
                    </Td>
                    <Td style={{ fontSize: '0.85rem' }}>{row.entitas}</Td>
                    <Td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {row.entitas_id ?? '—'}
                    </Td>
                    <Td style={{ fontSize: '0.85rem' }}>
                      {row.pegawai_nama ?? (row.pegawai_id ? `ID: ${row.pegawai_id}` : '—')}
                    </Td>
                    <Td
                      style={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.85rem',
                      }}
                    >
                      {row.alasan ?? '—'}
                    </Td>
                    <Td
                      style={{
                        maxWidth: 250,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.8rem',
                      }}
                    >
                      {formatDetail(row.detail)}
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {totalPages > 1 && <Pagination page={page} totalPages={totalPages} onChange={setPage} />}
        </Card>
      </div>
    </AdminLayout>
  );
}
